/**
 * Jira — the fourth reference semantic connector profile.
 *
 * Miro (canvas), Notion (doc tree), and Linear (issue tracker) proved the
 * abstraction; Jira is a second issue tracker on the SAME shared reference
 * builder — the confirmation that "reference dialect" is a real category, not a
 * per-vendor special case. Jira's `issuelinks` (blocks / relates / duplicates)
 * are its connector, its epic/parent chain is its containment.
 *
 * The gap this closes: the flat Jira import (importFromJira) reads issues but
 * not their links or parent, so blocking/hierarchy structure never becomes
 * canonical edges. Identity was already registered by the flat path (set 3);
 * this adds the relationship + structure planes on top.
 *
 * Category is intentionally NOT a plane here: Jira's status colour encodes
 * workflow phase (to-do/in-progress/done), not item semantics, so forcing a
 * category from it would fabricate meaning. Category stays with the flat Jira
 * field-mapping set (getDefaultJiraMappingSet) and human review.
 */

import type { ConnectorProfile } from '../profileTypes';
import type { RelationshipCueRule, TypeMapRule } from '../../fieldMappingRegistry';

export const JIRA_PROFILE: ConnectorProfile = {
  connectorType: 'jira',
  profileVersion: 1,

  nativeObjectTypes: ['issue', 'subtask', 'epic', 'issue_link', 'project'],
  relationshipModel: 'references',
  visualChannels: [], // Jira status colour is workflow phase, not item semantics
  spatialModel: 'tree',
  acquisition: 'api',

  fidelity: {
    content: {
      export: 'full',
      import: 'full',
      notes: 'Issue summary imports as title; the ADF description is flattened to text.',
    },
    structure: {
      export: 'partial',
      import: 'partial',
      notes: 'Project → Collection; `fields.parent` → `contains` edges (covers subtasks universally and epic→story on modern/team-managed projects). The classic epic-link custom field is not read — declared partial.',
    },
    relationships: {
      export: 'none',
      import: 'partial',
      notes: 'Issue links map to canonical edges by their directional name (blocks → dependency, is blocked by → prerequisite, relates → related, duplicates → related). Export writes epics, not links.',
    },
    semantics: {
      export: 'none',
      import: 'none',
      notes: 'No category is inferred: Jira status colour encodes workflow phase, not item semantics. Category is left to the flat field-mapping set and review.',
    },
  },

  identity: { stableExternalIds: true, deepLinks: true, appMetadata: false },
  continuity: { webhooks: true, comments: false },
};

// ── Profile-shipped default maps (workspace v3 rules override) ────────────────

export const JIRA_DEFAULT_TYPE_MAP: TypeMapRule[] = [
  { nativeType: 'issue', target: 'item' },
  { nativeType: 'subtask', target: 'item' },
  { nativeType: 'epic', target: 'region' },
];

/**
 * Jira issue-link cues are normalized to a fixed vocabulary by the engine read
 * (normalizeJiraLinkCue): 'blocks' / 'blocked_by' / 'related' / 'duplicate'.
 * 'blocks' is directed (source blocks target); 'blocked_by' inverts it to a
 * prerequisite — the same split as Linear. The parent chain synthesizes a
 * containment cue.
 */
export const JIRA_DEFAULT_RELATIONSHIP_MAP: RelationshipCueRule[] = [
  { cue: { kind: 'link_type', value: 'blocks' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'blocked_by' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'related' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'duplicate' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 0.9, basis: 'explicit' },
  // Epic / parent chain → containment.
  { cue: { kind: 'containment', value: 'parent' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 0.85, basis: 'visual_language' },
];

/**
 * An unmatched link type still becomes an edge — a generic `related` proposal —
 * rather than being dropped.
 */
export const JIRA_UNNAMED_RELATION = {
  canonicalType: 'related' as const,
  directionality: 'undirected' as const,
  defaultConfidence: 0.6,
  basis: 'spatial' as const,
};
